import { ORIGINAL_GAMES } from '@archon3d/content';
import { validateGameDefinition, type GameDefinition } from '@archon3d/core';

/** The game being edited, its account revision, and every draft the editor keeps. */
export const DRAFT = 'rpg-arena.editor-draft.v1';
export const DRAFT_META = 'rpg-arena.editor-draft-meta.v1';
export const DRAFT_LIST = 'rpg-arena.editor-drafts.v1';

export interface DraftEntry {
  definition: GameDefinition;
  revision?: string;
  accountVersion?: string;
}

/** What the browser keeps of the editor's work. */
export interface StoredDrafts {
  /** The game last edited, when the editor can open it. */
  current: GameDefinition | null;
  /** Every other draft the editor can open. */
  entries: DraftEntry[];
  /**
   * Drafts this version cannot open, exactly as they were stored: kept in the list, never
   * rewritten, and offered as a backup (editor:quality#2). The last edited game is among them when
   * it cannot be opened, so choosing another game does not lose it.
   */
  unreadable: unknown[];
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * The draft as the editor may open it: sound in its structure, whether or not it is playable yet
 * (editor:bugs#2). The check runs on a probe that fills in what an unfinished game may lack: a
 * name, a piece for a side that has none yet, player limits in range, a hex grid for more than
 * four sides. The editor's own problem list still tells the author what the game lacks. Null for
 * something this version cannot open, and for a copy of an original, which is read-only.
 */
export function readableDraft(value: unknown): GameDefinition | null {
  if (!isRecord(value) || value.version !== 1 || typeof value.id !== 'string') return null;
  if (ORIGINAL_GAMES.some((g) => g.id === value.id)) return null;
  try {
    const probe = structuredClone(value) as unknown as GameDefinition;
    probe.name = probe.name.trim() || 'Draft';
    probe.board.name = probe.board.name.trim() || 'Draft';
    for (const arena of probe.arenas) arena.name = arena.name.trim() || 'Draft arena';
    const board = probe.board;
    // A six-side draft may be switched to squares for a while.
    if (board.factions.length > 4 && board.grid !== 'hex') {
      board.grid = 'hex';
      board.walls = [];
    }
    // A side without pieces yet: one stands in for the check, on the first free floor square.
    const piece = probe.characters.find((c) => !c.summoned)?.id;
    const taken = new Set(board.placements.map((p) => `${p.x},${p.y}`));
    const free: { x: number; y: number }[] = [];
    board.cells.forEach((row, y) =>
      [...row].forEach((cell, x) => {
        if (cell !== '#' && !taken.has(`${x},${y}`)) free.push({ x, y });
      }),
    );
    for (const faction of board.factions) {
      if (board.placements.some((p) => p.faction === faction.id)) continue;
      const at = free.shift();
      if (!piece || !at) break;
      board.placements.push({ faction: faction.id, character: piece, ...at });
    }
    const sides = board.factions.length;
    const clamp = (n: number) => Math.min(sides, Math.max(2, Math.round(n)));
    probe.players = { min: clamp(probe.players.min), max: clamp(probe.players.max) };
    if (probe.players.min > probe.players.max) probe.players.min = probe.players.max;
    validateGameDefinition(probe);
    return value as unknown as GameDefinition;
  } catch {
    return null;
  }
}

/** Reads the editor's drafts, keeping what it cannot open aside rather than dropping it. */
export function readDrafts(storage: Pick<Storage, 'getItem'>): StoredDrafts {
  const read = (key: string): unknown => {
    try {
      const text = storage.getItem(key);
      return text === null ? null : JSON.parse(text);
    } catch {
      return undefined;
    }
  };
  const unreadable: unknown[] = [];
  const entries: DraftEntry[] = [];
  const list = read(DRAFT_LIST);
  for (const raw of Array.isArray(list) ? list : []) {
    const definition = isRecord(raw) ? readableDraft(raw.definition) : null;
    if (!definition) {
      // A copy of an original is not a draft: nothing is lost by leaving it out.
      const id = isRecord(raw) && isRecord(raw.definition) ? raw.definition.id : undefined;
      if (!ORIGINAL_GAMES.some((g) => g.id === id)) unreadable.push(raw);
      continue;
    }
    entries.push({
      definition,
      ...(typeof raw.revision === 'string' ? { revision: raw.revision } : {}),
      ...(typeof raw.accountVersion === 'string' ? { accountVersion: raw.accountVersion } : {}),
    });
  }
  const stored = read(DRAFT);
  const current = readableDraft(stored);
  // The last edited game is also in the list when it can be read; when it cannot, keep it here.
  if (
    stored !== null &&
    stored !== undefined &&
    !current &&
    !(isRecord(stored) && ORIGINAL_GAMES.some((g) => g.id === stored.id)) &&
    !unreadable.some(
      (raw) => isRecord(raw) && JSON.stringify(raw.definition) === JSON.stringify(stored),
    )
  )
    unreadable.push({ definition: stored });
  return { current, entries, unreadable };
}

/** The list as it is written back: the drafts in use, then the unreadable ones as they were. */
export function draftList(
  drafts: readonly GameDefinition[],
  meta: (id: string) => { revision?: string; accountVersion?: string } | undefined,
  unreadable: readonly unknown[],
): unknown[] {
  return [...drafts.map((definition) => ({ definition, ...meta(definition.id) })), ...unreadable];
}
